import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Loader, Select } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconCheck, IconAlertTriangle, IconShieldCheck } from "@tabler/icons-react";
import { FiCalendar } from "react-icons/fi";
import { useSelector } from "react-redux";
import moment from "moment";
import { updateFillMedication } from "../../services";
import { decrypt } from "../../utils";
import swal from "sweetalert";

// ── Tiny helpers ──────────────────────────────────────────────────────────────

const toValidDate = (val) => {
  if (!val) return null;
  const d = val instanceof Date ? val : new Date(val);
  return isNaN(d.getTime()) ? null : d;
};

const inpStyle = {
  height: 40, width: "100%", padding: "0 12px",
  border: "1px solid #D4D4D4", borderRadius: 6, fontSize: 14, color: "#171717",
  fontFamily: "Inter, system-ui, sans-serif", background: "#FFFFFF",
  outline: "none", boxSizing: "border-box",
};

const FieldGroup = ({ label, required, children, hint }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    {label && (
      <label style={{ fontSize: 13, fontWeight: 500, color: "#344054", fontFamily: "Inter" }}>
        {label}{required && <span style={{ color: "#DC2626" }}> *</span>}
      </label>
    )}
    {children}
    {hint && <div style={{ fontSize: 12, color: "#737373" }}>{hint}</div>}
  </div>
);

const FormRow = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>{children}</div>
);

// Number stepper – +/- buttons with center input
const Stepper = ({ value, onChange, min = 0, max = 9999, step = 1, size = "md" }) => {
  const h = size === "lg" ? 52 : size === "sm" ? 34 : 42;
  const fs = size === "lg" ? 24 : 16;
  const iw = size === "lg" ? 96 : 76;
  const btn = {
    width: h, height: h, flex: `0 0 ${h}px`,
    background: "#FFFFFF", border: 0, cursor: "pointer", color: "#171717",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
  };
  return (
    <div style={{
      display: "inline-flex", alignItems: "stretch", height: h,
      border: "1px solid #D4D4D4", borderRadius: 8, overflow: "hidden",
      background: "#FFFFFF", width: h * 2 + iw,
    }}>
      <button type="button" onClick={() => onChange(Math.max(min, value - step))}
        style={{ ...btn, borderRight: "1px solid #E5E5E5" }} aria-label="decrease">
        <svg width="14" height="14" viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12" stroke="#171717" strokeWidth="2" strokeLinecap="round" /></svg>
      </button>
      <input type="text" inputMode="numeric" pattern="[0-9]*" value={value}
        onChange={(e) => {
          const n = parseInt(e.target.value.replace(/[^0-9]/g, ""), 10);
          onChange(Math.max(min, Math.min(max, isNaN(n) ? 0 : n)));
        }}
        style={{
          height: h, width: iw, flex: `0 0 ${iw}px`,
          border: 0, textAlign: "center", fontSize: fs, fontWeight: 700,
          color: "#171717", outline: "none", fontFamily: "Inter",
          fontVariantNumeric: "tabular-nums", background: "transparent",
        }}
      />
      <button type="button" onClick={() => onChange(Math.min(max, value + step))}
        style={{ ...btn, borderLeft: "1px solid #E5E5E5" }} aria-label="increase">
        <svg width="14" height="14" viewBox="0 0 24 24">
          <line x1="5" y1="12" x2="19" y2="12" stroke="#171717" strokeWidth="2" strokeLinecap="round" />
          <line x1="12" y1="5" x2="12" y2="19" stroke="#171717" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
};

// Pill bottle SVG visualization
const PillBottle = ({ pct = 50, size = 140, narcotic = false }) => {
  const fillColor = narcotic ? "#DC2626" : "#FFBA45";
  const fillH = (size - 50) * (Math.max(0, Math.min(100, pct)) / 100);
  const w = size * 0.7;
  return (
    <svg width={w} height={size} viewBox={`0 0 ${w} ${size}`} style={{ display: "block" }}>
      <rect x={size * 0.15} y={4} width={size * 0.4} height={18} rx={3} fill="#404040" />
      <rect x={size * 0.08} y={22} width={size * 0.54} height={size - 28} rx={8}
        fill="#FFFFFF" stroke="#D4D4D4" strokeWidth={1.5} />
      <rect x={size * 0.08 + 3} y={size - 6 - fillH} width={size * 0.54 - 6} height={fillH}
        fill={fillColor} opacity={0.85}
        style={{ transition: "all 300ms ease" }} />
      <rect x={size * 0.08} y={size * 0.45} width={size * 0.54} height={size * 0.25}
        fill="#FFFFFF" opacity={0.7} />
      <line x1={size * 0.12} x2={size * 0.55} y1={size * 0.55} y2={size * 0.55}
        stroke="#A3A3A3" strokeWidth={1} />
      <line x1={size * 0.12} x2={size * 0.5} y1={size * 0.6} y2={size * 0.6}
        stroke="#A3A3A3" strokeWidth={1} />
    </svg>
  );
};

// Shared styles for Mantine DateInput / Select — matches inpStyle exactly
const mantineFieldStyles = {
  input: {
    fontFamily: "Inter, system-ui, sans-serif",
    fontSize: 14,
    height: 40,
    minHeight: 40,
    lineHeight: "38px",
    borderRadius: 6,
    border: "1px solid #D4D4D4",
    color: "#171717",
    background: "#FFFFFF",
    paddingLeft: 12,
    paddingRight: 12,
    "&:focus, &:focus-within": {
      borderColor: "#D4D4D4",
      outline: "none",
    },
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
    color: "#344054",
    fontFamily: "Inter, system-ui, sans-serif",
    marginBottom: 6,
  },
  wrapper: { width: "100%" },
  rightSection: { pointerEvents: "none" },
  item: {
    fontFamily: "Inter, system-ui, sans-serif",
    fontSize: 14,
    color: "#171717",
    "&[data-selected]": {
      background: "#FFF3DC",
      color: "#171717",
    },
    "&[data-hovered]": { background: "#F5F5F5" },
  },
};

// ── Main component ────────────────────────────────────────────────────────────

// Calculate avg daily doses from MAR doc timeSlot + form3 schedule fields
const calcAvgDaily = (med, marDoc) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dosagePerSlot = Number(med?.medicationadministered || med?.medicationAdministered) || 1;
  const timeSlots = marDoc?.timeSlot ?? med?.timeSlots;
  let activeSlotCount = 1;
  if (Array.isArray(timeSlots) && timeSlots.length > 0) {
    const activeSlots = timeSlots.filter((slot) => {
      if (!slot?.time) return false;
      const from = slot.effectiveFrom ? new Date(slot.effectiveFrom) : null;
      const to = slot.effectiveTo ? new Date(slot.effectiveTo) : null;
      if (from) { from.setHours(0,0,0,0); if (from > today) return false; }
      if (to) { to.setHours(0,0,0,0); if (to < today) return false; }
      return true;
    });
    activeSlotCount = activeSlots.length || 1;
  }

  const qtyPerDay = activeSlotCount * dosagePerSlot;

  const scheduleType = med?.scheduleType || "daily";
  if (scheduleType === "daily") return qtyPerDay;

  const repeatEvery = Number(med?.repeatEvery) || 1;
  const repeatType = med?.repeatType || "day";

  if (repeatType === "day") return qtyPerDay / repeatEvery;
  if (repeatType === "week") {
    const daysPerCycle = Array.isArray(med?.repeatOn) ? med.repeatOn.length || 1 : 1;
    return (daysPerCycle * qtyPerDay) / (repeatEvery * 7);
  }
  if (repeatType === "month") return qtyPerDay / (repeatEvery * 30);
  return qtyPerDay;
};

// For custom schedules, days supply counts only scheduled administration days.
// Returns doses per scheduled day (slotCount × dosePerSlot), ignoring repeatEvery/repeatOn.
const calcDosesPerAdminDay = (med, marDoc) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const timeSlots = marDoc?.timeSlot ?? med?.timeSlots;
  let slotCount = 1;
  if (Array.isArray(timeSlots) && timeSlots.length > 0) {
    const activeSlots = timeSlots.filter((slot) => {
      if (!slot?.time) return false;
      const from = slot.effectiveFrom ? new Date(slot.effectiveFrom) : null;
      const to = slot.effectiveTo ? new Date(slot.effectiveTo) : null;
      if (from) { from.setHours(0,0,0,0); if (from > today) return false; }
      if (to) { to.setHours(0,0,0,0); if (to < today) return false; }
      return true;
    });
    slotCount = activeSlots.length || 1;
  }
  const dosagePerSlot = Number(med?.medicationadministered || med?.medicationAdministered) || 1;
  return slotCount * dosagePerSlot;
};

const RefillDrawerStepper = ({ med, marDoc, clientId, onClose, onSave }) => {
  const [step, setStep] = useState(1);
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(false);

  const marRemaining = marDoc?.medicineRemaining ?? med?.medicineRemaining;
  const marTotal = marDoc?.totalMedicine ?? med?.totalMedicine;

  // const currentCount = marRemaining === "unlimited"
  // Read patient pharmacy from Redux selectedClient.form2.pharmacyName
  const { selectedClient } = useSelector((s) => s.mar);
  const { staff, allStaff } = useSelector((s) => s.staff);
  const { profile } = useSelector((s) => s.profile);
  const patientPharmacyName = selectedClient?.form2?.pharmacyName || null;

  const currentCount = med?.medicineRemaining === "unlimited"
    ? null
    : Number(marRemaining) || 0;
  const totalRef = marTotal === "unlimited"
    ? null
    : Number(marTotal) || 100;
  const isCustomSchedule = (med?.scheduleType || "daily") === "custom";
  const avgDaily = isCustomSchedule
    ? calcDosesPerAdminDay(med, marDoc)
    : (Number(med?.avgDailyUse) || calcAvgDaily(med, marDoc));
  const isNarcotic = med?.isNarcotic === "Yes" || med?.isNarcotic === true;
  const isUnlimited = marRemaining === "unlimited" || marTotal === "unlimited" || marDoc?.isUnlimited === true;

  const clientName = selectedClient?.name ? (decrypt(selectedClient.name) || selectedClient.name) : null;
  const clientPhoto = selectedClient?.imageurl ? (decrypt(selectedClient.imageurl) || selectedClient.imageurl) : null;
  const clientInitials = clientName ? clientName.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase() : "?";

  // Build pharmacy dropdown options: patient's pharmacy on file + "Other"
  const pharmacyOptions = patientPharmacyName
    ? [{ value: patientPharmacyName, label: patientPharmacyName }]
    : [];

  const firstPharmacyOption = patientPharmacyName || null;

  const assignedStaffIds = selectedClient?.staff
    ?.filter((s) => s?.isAssign === true)
    ?.map((s) => s?.staffId);

  const staffSource = allStaff?.length > 0 ? allStaff : (staff?.staffs || []);

  const adminOptions = (staffSource || [])
    .filter((s) => s?.type !== "staff" && s?.name)
    .map((s) => ({ value: s.name, label: `${s.name} (Admin)` }));

  const assignedStaffOptions = (staffSource || [])
    .filter((s) => s?.type === "staff" && s?.name && assignedStaffIds?.includes(s?.id))
    .map((s) => ({ value: s.name, label: s.name }));

  // Fallback: add logged-in admin from profile if allStaff didn't include them
  const adminName = profile?.user?.name;
  const adminAlreadyListed = adminOptions.some((o) => o.value === adminName);
  if (adminName && !adminAlreadyListed) {
    adminOptions.unshift({ value: adminName, label: `${adminName} (Admin)` });
  }

  const witnessOptions = [
    { value: "", label: "None" },
    ...adminOptions,
    ...assignedStaffOptions,
  ];

  const [physicalCount, setPhysicalCount] = useState(currentCount ?? 0);
  const [qty, setQty] = useState(totalRef ?? 60);
  const [pharmacySelect, setPharmacySelect] = useState(firstPharmacyOption);
  const [refillDate, setRefillDate] = useState(new Date());
  const [expiryDate, setExpiryDate] = useState(null);
  const [lot, setLot] = useState(med?.lotNumber || "");
  const [witness, setWitness] = useState(undefined);

  // Reset when med changes
  useEffect(() => {
    setStep(1);
    setVerified(false);
    setPhysicalCount(currentCount ?? 0);
    setQty(totalRef ?? 60);
    setPharmacySelect(firstPharmacyOption);

    setRefillDate(new Date());
    setExpiryDate(null);
    setLot(med?.lotNumber || "");
    setWitness(undefined);
  }, [med?.id]);

  // Close on Escape, lock body scroll
  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onEsc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const resolvedPharmacy = pharmacySelect || null;

  const newCount = isUnlimited ? null : physicalCount + qty;
  const newTotal = (totalRef !== null && !isUnlimited) ? totalRef + qty : null;
  const physMatchesSystem = physicalCount === currentCount;
  const displayCount = step === 1 ? physicalCount : newCount;
  const displayDenominator = step === 1 ? totalRef : newTotal;
  const displayPct = displayDenominator ? ((displayCount ?? 0) / displayDenominator) * 100 : 50;
  const newDays = newCount && avgDaily ? Math.max(1, Math.round(newCount / avgDaily)) : null;

  const steps = [
    { n: 1, label: "Verify" },
    { n: 2, label: "Add stock" },
    { n: 3, label: isNarcotic ? "Witness" : "Confirm" },
  ];

  const canContinue =
    (step === 1 && verified) ||
    (step === 2 && qty > 0);

  const handleConfirm = async () => {
    if (qty <= 0) return;
    setLoading(true);
    const payload = {
      medicationId: med?.id,
      medicationName: med?.medicationName,
      clientId,
      count: Number(qty),
      pharmacy: resolvedPharmacy || undefined,
      lotNumber: lot || undefined,
      medicationEndDate: expiryDate ? moment(expiryDate).endOf("D").toISOString() : null,
      hasExpiryDate: expiryDate ? true : false,

      refillDate: refillDate ? moment(refillDate).startOf("day").toISOString() : moment().startOf("day").toISOString(),
      refillBy: profile?.user?.name || undefined,
      witness: witness || undefined,
    };
    const resp = await updateFillMedication(payload);
    setLoading(false);
    if (resp) {
      await swal({
        title: "Refill recorded",
        text: `${med?.medicationName} has been refilled. New count: ${newCount ?? "updated"}.`,
        icon: "success",
        timer: 2500,
        buttons: false,
      });
      onSave?.();
      onClose();
    }
  };

  const medName = med?.medicationName || "Medication";
  const medStrength = med?.strengthAndForm || med?.dosage + " " + med?.unit || "—";

  return ReactDOM.createPortal(
    <>
      {/* Overlay */}
      <div onClick={onClose} style={{
        position: "fixed", inset: 0,
        background: "rgba(16,24,40,0.45)", zIndex: 1090,
      }} />

      {/* Drawer */}
      <aside style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: 640,
        background: "#FFFFFF", zIndex: 1091,
        boxShadow: "0 20px 40px -10px rgba(16,24,40,0.22)",
        display: "flex", flexDirection: "column",
        fontFamily: "Inter, system-ui, sans-serif",
        animation: "rDS_slideIn 240ms ease-out",
      }}>
        <style>{`
          @keyframes rDS_slideIn { from { transform: translateX(40px); opacity: 0.4 } to { transform: translateX(0); opacity: 1 } }
          @keyframes rDS_pillDrop {
            0%   { transform: translate(var(--rDS_x), -24px) rotate(var(--rDS_rot)); opacity: 0; }
            25%  { opacity: 1; }
            100% { transform: translate(var(--rDS_x), 28px) rotate(var(--rDS_rot)); opacity: 0; }
          }
          @keyframes rDS_countPop { 0% { transform: scale(1); } 40% { transform: scale(1.06); } 100% { transform: scale(1); } }
        `}</style>

        {/* ── Step indicator ── */}
        <div style={{
          padding: "18px 24px", borderBottom: "1px solid #E5E5E5",
          display: "flex", alignItems: "center", gap: 16, flexShrink: 0,
        }}>
          <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {steps.map((s, i) => {
                const active = step === s.n;
                const done = step > s.n;
                return (
                  <React.Fragment key={s.n}>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <span style={{
                        width: 28, height: 28, borderRadius: 999,
                        display: "grid", placeItems: "center",
                        fontSize: 12, fontWeight: 700, flexShrink: 0,
                        background: done ? "#171717" : active ? "#FFBA45" : "#FFFFFF",
                        border: `1.5px solid ${done ? "#171717" : active ? "#FFBA45" : "#E5E5E5"}`,
                        color: done ? "#FFFFFF" : active ? "#171717" : "#667085",
                        boxShadow: active ? "0 0 0 4px #FFF4DA" : "none",
                        transition: "all 180ms ease",
                      }}>
                        {done ? <IconCheck size={13} /> : s.n}
                      </span>
                      <span style={{
                        fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap",
                        color: active ? "#171717" : done ? "#262626" : "#667085",
                      }}>{s.label}</span>
                    </div>
                    {i < steps.length - 1 && (
                      <div style={{
                        width: 28, height: 1.5, flexShrink: 0, borderRadius: 2,
                        background: done ? "#171717" : "#E5E5E5",
                      }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{
            width: 34, height: 34, borderRadius: 10, border: "none",
            background: "#F2F4F7", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#475467" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* ── Medication header ── */}
        <div style={{
          padding: "18px 28px 16px", borderBottom: "1px solid #EAECF0", flexShrink: 0,
        }}>
          {clientName && (
            <div style={{ display: "flex", alignItems: "stretch", flexDirection: "column", gap: 10, marginBottom: 10 }}>
              <div style={{
                width: 60, height: 60, borderRadius: "50%", flexShrink: 0,
                overflow: "hidden", background: "#E5E7EB",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 14, fontWeight: 700, color: "#475467", fontFamily: "Inter",
              }}>
                {clientPhoto
                  ? <img src={clientPhoto} alt={clientName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : clientInitials}
              </div>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#344054", fontFamily: "Inter" }}>{clientName}</span>
            </div>
          )}
          <h3 style={{
            margin: 0, fontSize: 20, fontWeight: 700, color: "#171717",
            display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            letterSpacing: "-0.01em",
          }}>
            {medName}
            {medStrength && <span style={{ color: "#475467", fontWeight: 600 }}>{medStrength}</span>}
            {isNarcotic && (
              <span style={{
                fontSize: 11, fontWeight: 700, padding: "3px 10px",
                background: "#FEE2E2", color: "#B91C1C", borderRadius: 999,
                border: "1px solid #FECACA", letterSpacing: 0.3,
              }}>Narcotics</span>
            )}
          </h3>
        </div>

        {/* ── Scrollable body ── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "24px 28px 28px" }}>

          {/* Hero card */}
          {!isUnlimited && (
            <div style={{
              padding: "20px 22px", borderRadius: 14, marginBottom: 24,
              background: isNarcotic
                ? "linear-gradient(135deg, #FEF2F2 0%, #FFF7ED 100%)"
                : "linear-gradient(135deg, #FFF8E7 0%, #FFFAEB 100%)",
              border: "1px solid #FFE4A8",
              display: "flex", alignItems: "center", gap: 20,
            }}>
              <div style={{ position: "relative", flexShrink: 0, width: 80, height: 120 }}>
                <PillBottle pct={displayPct} size={130} narcotic={isNarcotic} />
                {step === 2 && qty > 0 && (
                  <div style={{
                    position: "absolute", top: 4, left: "50%", transform: "translateX(-50%)",
                    width: 56, height: 32, pointerEvents: "none", overflow: "hidden",
                  }}>
                    {[0, 1, 2, 3].map(i => (
                      <span key={i} style={{
                        position: "absolute", top: 0, left: "50%",
                        width: 11, height: 6, borderRadius: 4,
                        background: isNarcotic ? "#DC2626" : "#FFBA45",
                        "--rDS_x": `${(i - 1.5) * 8}px`,
                        "--rDS_rot": `${(i % 2 === 0 ? 1 : -1) * 16}deg`,
                        animation: `rDS_pillDrop 1.4s ease-in ${i * 0.18}s infinite`,
                      }} />
                    ))}
                  </div>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#92400E", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {step === 1 ? "Current count" : step === 2 ? "After this refill" : "Final total"}
                </div>
                <div key={`${step}-${displayCount}`} style={{
                  display: "flex", alignItems: "baseline", gap: 8, marginTop: 6,
                  animation: "rDS_countPop 320ms ease-out",
                }}>
                  <span style={{
                    fontSize: 42, fontWeight: 700, color: "#171717",
                    fontVariantNumeric: "tabular-nums", lineHeight: 1,
                  }}>{displayCount ?? "—"}</span>
                  {displayDenominator && (
                    <span style={{ fontSize: 17, color: "#98A2B3", fontWeight: 600 }}>
                      / {displayDenominator}
                    </span>
                  )}
                </div>
                {step === 2 && qty > 0 && (
                  <div style={{
                    marginTop: 10, display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "4px 12px", background: "#FFFFFF", border: "1px solid #BBF7D0",
                    borderRadius: 999, fontSize: 12, fontWeight: 600, color: "#15803D",
                  }}>
                    + {qty} added
                  </div>
                )}
                <div style={{ marginTop: 8, fontSize: 13, color: "#667085" }}>
                  {step === 1
                    ? `${avgDaily}/day avg use${physicalCount != null && avgDaily > 0 ? ` · ~${Math.max(1, Math.round(physicalCount / avgDaily))} days supply` : ""}`
                    : newDays ? `~${newDays} days supply after refill` : ""}
                </div>
              </div>
            </div>
          )}

          {/* ── Step 1: Verify ── */}
          {step === 1 && (
            <div>
              <h4 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "#171717" }}>
                Verify the current count
              </h4>
              <p style={{ margin: "0 0 16px", fontSize: 13, color: "#475467" }}>
                Physically count what's on hand. Discrepancies will be flagged in the audit log.
              </p>

              {!isUnlimited ? (
                <>
                  <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                    {/* <div style={{ flex: 1, padding: 14, border: "1px solid #EAECF0", borderRadius: 10, background: "#FFFFFF" }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#667085", textTransform: "uppercase", letterSpacing: 0.4 }}>System</div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: "#171717", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{currentCount}</div>
                    </div> */}
                    <div style={{
                      flex: 1, padding: 14, borderRadius: 10,
                      border: physMatchesSystem ? "1px solid #BBF7D0" : "1px solid #FECACA",
                      background: physMatchesSystem ? "#F0FDF4" : "#FEF2F2",
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, color: physMatchesSystem ? "#15803D" : "#B91C1C" }}>Physical</div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: "#171717", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{physicalCount}</div>
                    </div>
                  </div>

                  <FieldGroup label="Physical count" required hint="Count each unit individually">
                    <Stepper value={physicalCount} onChange={setPhysicalCount} step={1} max={9999} size="md" />
                  </FieldGroup>

                  {!physMatchesSystem && (
                    <div style={{
                      marginTop: 14, padding: 12, background: "#FEF2F2", border: "1px solid #FECACA",
                      borderRadius: 10, display: "flex", gap: 10,
                    }}>
                      <IconAlertTriangle size={16} color="#B91C1C" style={{ flexShrink: 0, marginTop: 1 }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#B91C1C" }}>
                          Discrepancy: {physicalCount > currentCount ? "+" : ""}{physicalCount - currentCount}
                        </div>
                        <div style={{ fontSize: 12, color: "#7F1D1D", marginTop: 2 }}>
                          Will be flagged in the audit log{isNarcotic ? " and reported per CII protocol" : ""}.
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{
                  padding: 14, background: "#F9FAFB", border: "1px solid #EAECF0", borderRadius: 10,
                  fontSize: 13, color: "#667085",
                }}>
                  This medication is tracked as unlimited — no count verification needed.
                </div>
              )}

              <label style={{
                display: "flex", alignItems: "center", gap: 10, marginTop: 16, padding: 12,
                border: "1px solid #EAECF0", borderRadius: 10, cursor: "pointer",
                background: verified ? "#FFFAEB" : "#FFFFFF", transition: "background 150ms ease",
              }}>
                <input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)}
                  style={{ accentColor: "#FFBA45", width: 18, height: 18, flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: "#171717" }}>
                  {isUnlimited
                    ? "I confirm this medication is ready for refill."
                    : "I personally verified the physical count matches what's entered above."}
                </span>
              </label>
            </div>
          )}

          {/* ── Step 2: Add stock ── */}
          {step === 2 && (
            <div>
              <h4 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "#171717" }}>
                Add new stock
              </h4>
              <p style={{ margin: "0 0 16px", fontSize: 13, color: "#475467" }}>
                Enter the quantity received and lot details from the packaging.
              </p>

              <FieldGroup label="Quantity received" required>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Stepper value={qty} onChange={setQty} step={1} max={9999} size="lg" />
                  <span style={{ fontSize: 14, color: "#475467" }}>units</span>
                </div>
              </FieldGroup>

              {!isUnlimited && (
                <div style={{
                  marginTop: 14, padding: "12px 16px", background: "#F9FAFB", border: "1px solid #EAECF0",
                  borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-around", gap: 10,
                }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 11, color: "#667085", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>On hand</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#171717", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{physicalCount}</div>
                  </div>
                  <div style={{ fontSize: 18, color: "#98A2B3" }}>+</div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 11, color: "#15803D", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>Adding</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#15803D", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{qty}</div>
                  </div>
                  <div style={{ fontSize: 18, color: "#98A2B3" }}>=</div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 11, color: "#171717", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>Total</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "#171717", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{newCount}</div>
                  </div>
                </div>
              )}

              <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
                {/* Row 1: Pharmacy | Refill date */}
                <FormRow>
                  <Select
                    label="Pharmacy"
                    placeholder="Select pharmacy"
                    data={pharmacyOptions}
                    value={pharmacySelect}
                    onChange={setPharmacySelect}
                    clearable
                    styles={mantineFieldStyles}
                    withinPortal
                    zIndex={1100}
                  />
                  <DateInput
                    label="Refill date"
                    placeholder="Pick a date"
                    value={refillDate}
                    onChange={setRefillDate}
                    icon={<FiCalendar size={14} />}
                    styles={mantineFieldStyles}
                    withinPortal
                    zIndex={1100}
                  />
                </FormRow>

                {/* Row 2: Lot number | Expiration date */}
                <FormRow>
                  <FieldGroup label="Lot number">
                    <input value={lot} onChange={(e) => setLot(e.target.value)}
                      placeholder="LOT-XXXXX" style={inpStyle} />
                  </FieldGroup>
                  <DateInput
                    label="Expiration date"
                    placeholder="Pick a date"
                    value={expiryDate}
                    onChange={setExpiryDate}
                    minDate={new Date()}
                    clearable
                    icon={<FiCalendar size={14} />}
                    styles={mantineFieldStyles}
                    withinPortal
                    zIndex={1100}
                  />
                </FormRow>
              </div>
            </div>
          )}

          {/* ── Step 3: Confirm / Witness ── */}
          {step === 3 && (
            <div>
              <h4 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "#171717" }}>
                {isNarcotic ? "Witness & confirm" : "Confirm refill"}
              </h4>
              <p style={{ margin: "0 0 16px", fontSize: 13, color: "#475467" }}>
                {isNarcotic
                  ? "A licensed witness must verify the count before confirming."
                  : "Review the details. On confirm, this refill is added to the medication log."}
              </p>

              {/* Summary */}
              <div style={{ border: "1px solid #EAECF0", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
                <div style={{
                  padding: "10px 14px", background: "#F9FAFB", borderBottom: "1px solid #EAECF0",
                  fontSize: 11, fontWeight: 600, color: "#667085", textTransform: "uppercase", letterSpacing: 0.4,
                }}>Refill summary</div>
                <div style={{ padding: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  {[
                    ["Medication", `${medName}${medStrength ? " · " + medStrength : ""}`],
                    ["Quantity added", `+${qty}`],
                    ["Pharmacy", resolvedPharmacy || "—"],
                    ["Refill date", toValidDate(refillDate)?.toLocaleDateString("en-US") ?? "—"],
                    ["Lot number", lot || "—"],
                    ["Expiration", toValidDate(expiryDate)?.toLocaleDateString("en-US") ?? "—"],
                    !isUnlimited && newCount != null
                      ? ["New count", `${newCount}${newDays ? ` (~${newDays}d)` : ""}`]
                      : ["Count", "Unlimited"],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span style={{ fontSize: 11, color: "#667085", fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.4 }}>{k}</span>
                      <span style={{ fontSize: 13, color: "#171717", fontWeight: 500, wordBreak: "break-all" }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              {isNarcotic ? (
                <div>
                  <FieldGroup label="Witness name" hint="Select the staff member witnessing this refill">
                    <Select
                      value={witness}
                      onChange={(val) => setWitness(val)}
                      data={witnessOptions}
                      placeholder="Select a witness"
                      styles={mantineFieldStyles}
                      withinPortal
                      zIndex={1100}
                    />
                  </FieldGroup>
                  {witness && (
                    <div style={{
                      marginTop: 12, padding: 12, background: "#FFFAEB", border: "1px solid #FDE68A",
                      borderRadius: 8, fontSize: 12, color: "#92400E", display: "flex", gap: 8,
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 1 }}>
                        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      Witness <strong>{witness}</strong> will be recorded on this refill entry.
                    </div>
                  )}
                </div>
              ) : (
                <div style={{
                  padding: 12, background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10,
                  fontSize: 13, color: "#15803D", display: "flex", gap: 10, alignItems: "flex-start",
                }}>
                  <IconShieldCheck size={16} color="#15803D" style={{ flexShrink: 0, marginTop: 1 }} />
                  Confirming will record this refill in the medication log under your account.
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{
          padding: "16px 28px", borderTop: "1px solid #EAECF0",
          display: "flex", gap: 10, alignItems: "center", background: "#FFFFFF", flexShrink: 0,
        }}>
          <button type="button" onClick={() => step === 1 ? onClose() : setStep(step - 1)} style={{
            height: 40, padding: "0 18px", border: "1px solid #D0D5DD", borderRadius: 8,
            background: "#FFFFFF", color: "#344054", fontSize: 14, fontWeight: 600,
            cursor: "pointer", fontFamily: "Inter",
          }}>
            {step === 1 ? "Cancel" : "Back"}
          </button>
          <span style={{ flex: 1 }} />
          {step < 3 ? (
            <button type="button" disabled={!canContinue} onClick={() => setStep(step + 1)} style={{
              height: 40, padding: "0 22px", border: "none", borderRadius: 8,
              background: canContinue ? "#FFBA45" : "#E5E5E5",
              color: canContinue ? "#171717" : "#A3A3A3",
              fontSize: 14, fontWeight: 600, cursor: canContinue ? "pointer" : "not-allowed",
              fontFamily: "Inter", transition: "background 150ms ease",
            }}>
              Continue
            </button>
          ) : (
            <button type="button" onClick={handleConfirm} disabled={loading || (false)} style={{
              height: 40, padding: "0 22px", border: "none", borderRadius: 8,
              background: (loading || (false)) ? "#E5E5E5" : "#FFBA45",
              color: (loading || (false)) ? "#A3A3A3" : "#171717",
              fontSize: 14, fontWeight: 600, cursor: (loading || (false)) ? "not-allowed" : "pointer",
              fontFamily: "Inter", display: "inline-flex", alignItems: "center", gap: 8,
              opacity: loading ? 0.7 : 1,
            }}>
              {loading && <Loader size={14} color="dark" />}
              {isNarcotic ? "Sign & confirm refill" : "Confirm refill"}
            </button>
          )}
        </div>
      </aside>
    </>,
    document.body,
  );
};

export default RefillDrawerStepper;
